import Vue from 'vue'
import { cloneDeep, get } from 'lodash'
import { conversation } from '../helpers/consts'
import { getCurrentUrl, generateTimestamp, dispatchLatestUnreadMessage } from '../helpers/helpers'

import { trigger, triggerType } from '@/helpers/Tracker'
import {
  EVENT,
  listenToCustomEvent,
  dispatchCustomEvent
} from '@/helpers/customEvents'

// connectionStates
// if no activeConversation, we are a "fresh" enduser - show welcome message and send-message prompt
// activeConversation - chat in progress

// actions:
// init - initialize as "fresh" enduser
// connect - (re)connect and setup activeConversation
// sendMessage
// messageReceived
// loadMessages - (re)load all messages

const AIAIBOT_INPUT_DEFAULT_CONFIG = {
  inputType: 'text',
  maxLength: '4000',
  required: true,
  widgetType: 'intent'
}

const DEFAULT_AGENT = {
  id: null,
  screenName: 'Sanitas',
  type: 'default'
}

const AIAIBOT_AGENT = {
  id: null,
  name: 'Alva',
  agentName: 'Alva',
  screenName: 'Alva',
  type: 'default'
}

const aiaiBotMsgQueue = []

setInterval(() => {
  if (!aiaiBotMsgQueue.length) {
    return
  }

  const message = aiaiBotMsgQueue.shift()

  if (!message) {
    return
  }

  message.fn(...(message.args || []))

  if (typeof message.cb === 'function') {
    message.cb(...(message.cbArgs || []))
  }
}, process.env.VUE_APP_AIAIBOT_MSG_DELAY)

const registerCallbacks = ({ state, commit, dispatch, chatApi }) => {
  if (!state.apiCallbackRegistered) {
    chatApi.registerEventHandler((event) => {
      switch (event.type) {
        case 'MESSAGE':
          dispatch('messageReceived', event.payload)
          break
        case 'AGENT_JOINED_CONVERSATION':
          dispatch('addAgentToConversation', event.payload)
          dispatch('setAgentStatus', {
            status: event.payload.agentStatus
          })
          if (!event.payload.skipMessage) {
            dispatch('messageReceived', {
              msgType: 'JOIN',
              agentName: event.payload.agentName,
              screenName: event.payload.screenName,
              senderId: event.payload.senderId,
              msgId: event.payload.msgId,
              status: event.payload.agentStatus,
              pictureUrl: event.payload.pictureUrl
            })
          }
          break
        case 'AGENT_STATUS_CHANGED':
          dispatch('setAgentStatus', {
            status: event.payload.newStatus
          })
          break
        case 'CONVERSATION_HANDOVER':
          dispatch('removeAgentFromConversation')
          dispatch('addAgentToConversation', event.payload)
          dispatch('setAgentStatus', {
            status: event.payload.agentStatus
          })
          dispatch('messageReceived', {
            msgType: 'HANDOVER',
            agentName: event.payload.agentName,
            screenName: event.payload.screenName,
            msgId: event.payload.msgId
          })
          break
        case 'AGENT_LEFT_CONVERSATION':
          dispatch('messageReceived', {
            msgType: 'LEAVE',
            agentName: event.payload.agentName,
            screenName: event.payload.screenName,
            msgId: event.payload.msgId
          })
          dispatch('removeAgentFromConversation')
          break
        case 'WS_RECONNECTED':
          // we should reload msgs here
          console.log('Reloading messages after ws reconnect')
          dispatch('reloadMessages')
          break
        case 'CHATBOT_LEFT':
          dispatch('conversationClose')
          break
        case 'CONNECT_CHATBOT_ACTIVE_CONVERSATION':
          commit('CHATBOT_ACTIVE', {status: event.payload})
          break
        case 'SET_DISABLE_SEND_BUTTON':
          commit('SET_DISABLE_SEND_BUTTON', {status: event.payload})
          break
        case 'SET_USER_PROFILE':
          commit('SET_USER_PROFILE', event.payload)
          break
        case 'CHECK_ACTIVITY':
          dispatch('addCheckActivityMsg', event.payload)
          break
        case 'ARCHIVE_INACTIVE_CONVERSATION':
          dispatch(
            'uistate/setConversationState',
            'CONVERSATION_NEW',
            { root: true }
          )
          break
        case 'AIAIBOT_SESSION_INITIALIZED':
          dispatch('setAiaiBotSessionId', event.payload.sessionId)
          break
        case 'AIAIBOT_SESSION_NOT_INITIALIZED':
          dispatch('handleAiaiBotConversationStartFailed')
          break
        case 'AIAIBOT_INACTIVE_CLOSE':
          dispatch('handleInactiveConversationEnded')
          break
        default:
          console.warn('Unknown event type: ' + event.type)
      }
    })
    commit('SET_API_CALLBACK_REGISTERED', true)
  }
}

export default {
  namespaced: true,
  state: {
    user: {},
    conversation: null,
    archived: [],
    selectedArchived: {},
    selectedArchivedMessages: [],
    archivedLoading: true,
    unreadMessages: 0,
    apiCallbackRegistered: false,
    activeAgent: null,
    agentStatus: '',
    basket: {},
    showAiaiBotInput: false,
    aiaiBotInputConfig: {},
    typerActive: true,
    acceptedTerms: null,
    disableSendButton: false,
    waitMessageShown: false,
    closingTime: null,
    trigger: null,
    chatbotActive: false,
    chatbotIncluded: false,
    chatbotAnsweredQuestions: [],
    channel: '',
    rating: {
      star: null,
      comment: '',
      answer: true
    },
    lastCheckActivityActiveMsg: {},
    aiaiBotStoryId: null,
    waitingOnAiaiBot: false,
    isNonCustomer: null
  },
  getters: {
    isUserAuthenticated: (state) => state.user.customerId,
    getChannel: (state) => state.channel,
    getAgentStatus: (state, getters) => getters.isAiaiBotAgent
      ? 'online'
      : state.agentStatus && state.agentStatus.toLowerCase(),
    startMessages (state, getters, rootState, rootGetters) {
      const isCustomerFlow = rootGetters['uistate/isCustomerFlow']
      const isAiaiBotActive = rootGetters['uistate/isAiaiBotActive']
      const isHandover = (
        state.conversation.conversationType === 'CHAT' &&
        isAiaiBotActive &&
        Boolean(state.aiaiBotStoryId) &&
        state.conversation.messages.length
      )

      if (isCustomerFlow) {
        if (isAiaiBotActive || isHandover) {
          return []
        }

        if (rootState.uistate.outsideServiceHoursMessage) {
          return [{
            convId: -1,
            msgId: -101,
            msgText: rootState.uistate.outsideServiceHoursMessage,
            senderId: -2000,
            screenName: 'Sanitas',
            msgType: 'CHAT',
            status: 'SEEN'
          }]
        }

        return [{
          convId: -1,
          msgId: -101,
          msgLabelKey: 'welcome_message',
          senderId: -2000,
          screenName: 'Sanitas',
          msgType: 'CHAT',
          status: 'SEEN'
        }]
      }

      return [{
        convId: -1,
        msgId: -105,
        msgLabelKey: state.trigger === 'RETENTION' ? 'start_disclaimer_retention' : 'start_disclaimer',
        senderId: -2000,
        screenName: 'Sanitas',
        msgType: 'CHAT',
        status: 'SEEN'
      }, {
        convId: -1,
        msgId: -103,
        msgLabelKey: 'i_agree',
        senderId: -2000,
        screenName: 'User',
        msgType: 'CHAT',
        dummy_user: true,
        status: 'SEEN'
      },
      {
        convId: -1,
        msgId: -101,
        msgLabelKey: 'welcome_message',
        senderId: -2000,
        screenName: 'Sanitas',
        msgType: 'CHAT',
        status: 'SEEN'
      }]
    },
    disagreeMessages (state) {
      return [{
        convId: -1,
        msgId: -99,
        msgLabelKey: state.trigger === 'RETENTION' ? 'start_disclaimer_retention' : 'start_disclaimer',
        senderId: -2000,
        screenName: 'Sanitas',
        msgType: 'CHAT',
        status: 'SEEN'
      }, {
        convId: -1,
        msgId: -98,
        msgLabelKey: 'i_disagree',
        senderId: -2000,
        screenName: 'User',
        msgType: 'CHAT',
        dummy_user: true,
        status: 'SEEN'
      }]
    },
    getLatestAgent (state) {
      if (!state.conversation) {
        return DEFAULT_AGENT
      }

      let messages = cloneDeep(state.conversation.messages)
      let userId = state.conversation.userId

      if (!(messages && messages.length && userId)) {
        return DEFAULT_AGENT
      }

      return messages.reverse().reduce((agent, message) => {
        if (agent.id || message.senderId === userId || !(message.senderId > 0) || message.msgType !== 'CHAT') {
          return agent
        }
        return {
          id: message.senderId,
          agentName: message.agentName,
          screenName: message.screenName
        }
      }, DEFAULT_AGENT)
    },
    isAiaiBotAgent (state, getters, rootState, rootGetters) {
      const { conversation } = state
      const isAiaiBotActive = rootGetters['uistate/isAiaiBotActive']

      return (
        isAiaiBotActive &&
        Boolean(conversation?.aiaiBotSessionId) &&
        conversation?.conversationType === 'AIAIBOT' &&
        conversation?.status === 'ACTIVE'
      )
    },
    getActiveAgent (state, getters) {
      const { activeAgent } = state

      return activeAgent || (getters.isAiaiBotAgent ? AIAIBOT_AGENT : DEFAULT_AGENT)
    },
    agentGender (_, getters) {
      const { gender } = getters.getActiveAgent
      return gender ? gender.toLowerCase() : null
    },
    isActiveConversation ({ conversation }) {
      return conversation && conversation.status
        ? conversation.status === 'ACTIVE'
        : false
    },
    getClosingTime: state => state.closingTime,
    getUser: state => state.user,
    getChatbotAnsweredQuestions: state => state.chatbotAnsweredQuestions,
    getRating: state => state.rating,
    getArchived: state => state.archived,
    getArchivedLoading: state => state.archivedLoading,
    getSelectedArchived: state => state.selectedArchived,
    getUnreadMessages: state => (state.conversation?.messages || [])
      .filter(msg =>
        msg.msgType === 'CHAT' &&
        msg.senderId !== state.conversation.userId &&
        ['SENT', 'RECEIVED'].includes(msg.status)
      ),
    automaticMessageAgentScreenName: state => {
      const conversationType = state.conversation?.conversationType

      const participantAgent = conversationType === 'AIAIBOT'
        ? AIAIBOT_AGENT
        : state.conversation.participants.find(participant => participant.agent)

      return participantAgent
        ? participantAgent.name
        : 'Sanitas'
    }
  },
  actions: {
    addCheckActivityMsg ({ commit }, content) {
      commit('ADD_MESSAGE', content)
    },
    setActivity ({ state, dispatch }) {
      const channel = get(this._vm, '$chatApi.settings.channel')
      const message = {
        sender: state.user.userId,
        type: 'ENDUSER_IS_ACTIVE',
        channel
      }
      this._vm.$chatApi.sendMessage(message, state.conversation)
      dispatch('updateMessagesStatus')
      dispatch('reloadMessages')
    },
    sendMessage ({ state, commit, dispatch, rootState, rootGetters }, content) {
      // initialize chat if necessary
      console.log('Current state userID', state.user.userId)

      if (!state.user.userId) {
        dispatch('connect')
          .then(() => {
            dispatch('sendMessage', content)
          })
          .then(() => {
            trigger(triggerType.ACTION, 'ChatDiscussionStarted')

            // TODO: this needs to be passed by AEM --> for the moment been, it's hardcoded as suggested by George
            const premiumTranslations = ['praemien', 'prime', 'premi', 'premium']
            if (new RegExp(premiumTranslations.join('|')).test(getCurrentUrl())) {
              trigger(triggerType.ACTION, 'ChatOvpStarted', getCurrentUrl())
            }

            if (rootGetters['uistate/isCustomerFlow']) {
              setTimeout(() => {
                if (state.activeAgent === null && !state.waitMessageShown) {
                  this._vm.$chatApi.getChatAvailable(rootState.uistate.lang).then(result => {
                    if (state.activeAgent === null && !state.waitMessageShown && !state.chatbotIncluded) {
                      const message = {
                        msgLabelKey: result.isOpeningHours ? 'please_wait_push_message' : 'please_wait_push_message_outside_business_hours',
                        msgType: 'CHAT'
                      }
                      if (!result.isOpeningHours) {
                        message.msgText = result.outsideServiceHoursMessage
                      } else if (!result.isAgentAvailable) {
                        // this case is not relevant for SC -> normal message is displayed
                      }
                      commit('SET_WAIT_MESSAGE_SHOWN', true)
                      commit('ADD_MESSAGE', message)
                    }
                  })
                }
              }, 5000)
            } else {
              setTimeout(() => {
                if (state.activeAgent === null && state.chatbotIncluded === false) {
                  const message = {
                    msgLabelKey: 'please_wait_message',
                    msgType: 'CHAT'
                  }
                  commit('ADD_MESSAGE', message)
                }
              }, conversation.delayWaitMessage)
            }
          })
          .catch(e => {
            console.log('ERROR. Entering panic mode.', e)
            this._vm.$chatApi.panicMode()
          })
      } else {
        const channel = get(this._vm, '$chatApi.settings.channel')
        let message = {
          sender: state.user.userId,
          channel
        }
        if (content.msgType === 'AIAIBOT_INITIALIZE') {
          Object.assign(message, {
            conversationId: state.conversation.convId,
            value: content.content,
            type: content.msgType,
            content: state.aiaiBotStoryId
          })
          commit('SET_WAITING_ON_AIAIBOT', true)
        } else if (content.msgType === 'AIAIBOT_FEEDBACK') {
          Object.assign(message, {
            conversationId: state.conversation.convId,
            msgType: content.msgType,
            type: content.msgType,
            aiaibotPayload: {
              widgetType: content.widgetType,
              text: content.content
            }
          })

          commit('ADD_MESSAGE', message)
          commit('SET_WAITING_ON_AIAIBOT', true)
        } else if (content.msgType === 'CHATBOT') {
          Object.assign(message, {
            msgId: content.msgId,
            content: content.content,
            botPayload: content.botPayload,
            type: content.msgType
          })
        } else {
          Object.assign(message, {
            content,
            type: 'CHAT'
          })
        }
        this._vm.$chatApi.sendMessage(message, state.conversation)
      }
    },
    messageReceived ({ state, commit, rootState, dispatch }, msg) {
      dispatch('uistate/updateAgentTyping', { agentTyping: '', timestamp: 0 }, { root: true })

      if (state.user.userId && state.conversation.convId !== -1) {
        if (state.activeAgent &&
            msg.msgType === 'CHAT' &&
            state.user.userId !== Number(msg.senderId)
        ) {
          let message = {
            sender: state.user.userId,
            ...(state.activeAgent && { recipient: state.activeAgent.userId ? state.activeAgent.userId : state.activeAgent.senderId }),
            msgId: msg.msgId,
            content: 'RECEIVED',
            type: 'MESSAGE_STATUS'
          }

          commit('UPDATE_MESSAGE_STATUS', {
            messageId: msg.msgId,
            status: 'RECEIVED'
          })

          this._vm.$chatApi.sendConversationEvent(state.conversation.convId, message)

          if (state.typerActive && state.unreadMessages === 0) {
            let message = {
              sender: state.user.userId,
              ...(state.activeAgent && { recipient: state.activeAgent.userId ? state.activeAgent.userId : state.activeAgent.senderId }),
              msgId: msg.msgId,
              content: 'SEEN',
              type: 'MESSAGE_STATUS'
            }

            commit('UPDATE_MESSAGE_STATUS', {
              messageId: msg.msgId,
              status: 'SEEN'
            })

            this._vm.$chatApi.sendConversationEvent(state.conversation.convId, message)
          }
        }

        if (msg.msgType === 'AIAIBOT') {
          if (['intent', 'user-input'].includes(msg.payloadType)) {
            aiaiBotMsgQueue.push({
              fn: dispatch,
              args: ['setShowAiaiBotInput', true]
            }, {
              fn: dispatch,
              args: ['setAiaiBotInputConfig', {
                widgetType: msg.payloadType,
                config: {
                  ...AIAIBOT_INPUT_DEFAULT_CONFIG,
                  ...msg.payload
                }
              }],
              cb: commit,
              cbArgs: ['SET_WAITING_ON_AIAIBOT', false]
            })
          }

          // Weird payload here, need to refactor the backend :|
          // That's the session ended message (eventType='session_ended'):
          if (
            msg.payload?.payloadType === 'session' &&
            msg.payload?.payload?.sessionAction === 'end'
          ) {
            aiaiBotMsgQueue.push({
              fn: dispatch,
              args: ['handleConversationEnded']
            }, {
              fn: commit,
              args: ['SET_WAITING_ON_AIAIBOT', false]
            })
          }

          // Handover to live chat
          if (msg.payloadType === 'live-chat') {
            commit('SET_CONVERSATION', {
              ...state.conversation,
              conversationType: 'CHAT'
            })
            aiaiBotMsgQueue.push({
              fn: dispatch,
              args: ['handleConversationHandover']
            }, {
              fn: commit,
              args: ['SET_WAITING_ON_AIAIBOT', false]
            })
          }
        }
      }

      if (msg.msgType === 'JOIN' || msg.msgType === 'LEAVE') {
        commit('SET_AGENT_STATUS', msg)
      }

      if (msg.msgType === 'AIAIBOT') {
        aiaiBotMsgQueue.push({
          fn: commit,
          args: ['ADD_MESSAGE', msg],
          ...(['button-group', 'button-list'].includes(msg.payloadType)
            ? {
              cb: commit,
              cbArgs: ['SET_WAITING_ON_AIAIBOT', false]
            }
            : {}
          )
        })
      } else {
        commit('ADD_MESSAGE', msg)
      }

      if (!rootState.uistate.windowOpen || rootState.uistate.windowScrolledUp) {
        dispatch('updateUnreadMessages')
      }
    },
    reloadMessages ({ commit, state }) {
      if (state.conversation && state.conversation.convId) {
        this._vm.$chatApi.getMessages({...state.conversation })
          .then((result) => {
            let msgs = (result || []).reverse()
            msgs.forEach((msg) => {
              if (!state.conversation.messages.find(existingMsg => existingMsg.msgId === parseInt(msg.msgId, 10))) {
                commit('ADD_MESSAGE', msg)
              }
            })
            commit('SET_LAST_CHECK_ACTIVITY_MSG_STATUS', {})
          })
      }
    },
    sendTranscript ({ state, rootState }, payload) {
      return this._vm.$chatApi.sendTranscript(state.conversation.convId, rootState.uistate.lang, payload.email)
    },
    setAgentStatus ({ commit }, payload) {
      commit('SET_AGENT_STATUS', payload)
    },
    setRating ({ commit }, payload) {
      commit('SET_RATING', payload)
    },
    sendRating ({ commit, state }, payload) {
      const rating = {
        ...payload,
        star: payload.star || null
      }
      this._vm.$chatApi.sendRating(state.conversation.convId, rating)
        .then(() => {
          commit('uistate/SET_CONVERSATION_STATE', 'CONVERSATION_RATED', { root: true })
          commit('SET_RATING', {
            star: null,
            comment: '',
            answer: true
          })
        })
    },
    sendNpsRating ({ commit, state, dispatch }, payload) {
      this._vm.$chatApi.sendNpsRating(state.conversation.convId, payload)
        .then(() => {
          commit('uistate/SET_CONVERSATION_STATE', 'CONVERSATION_NEW', { root: true })
          dispatch('conversationClose')
        })
        .catch(err => {
          if (err.response && err.response.status === 400) { // ignore failure so that user is not stuck
            commit('uistate/SET_CONVERSATION_STATE', 'CONVERSATION_NEW', { root: true })
            dispatch('conversationClose')
          } else {
            console.log('sendNpsRating ERR', err)
          }
        })
    },
    async sendUrl ({ state }) {
      try {
        await this._vm.$chatApi.sendUrl(
          state.conversation, getCurrentUrl(),
          generateTimestamp()
        )
      } catch (e) {
        console.error('sendUrl error', e)
      }
    },
    sendBasket ({ state }) {
      this._vm.$chatApi.sendBasket(state.conversation.convId, state.basket)
    },
    init ({ commit, state, dispatch }, { session }) {
      // let timeDiff = state.closingTime - generateTimestamp()
      // if (timeDiff < 0) {
      //   dispatch('uistate/checkAgentAvailable', {}, {root: true})
      // } else {
      // TODO: commented because it might be fixed with RUN-342
      registerCallbacks({ state, commit, dispatch, chatApi: this._vm.$chatApi })

      if (session && session.convId) {
        return dispatch('connect')
      } else {
        let conv = {
          convId: -1,
          status: 'INIT',
          mine: true
        }
        commit('SET_CONVERSATION', conv)
        commit('SET_USER_PROFILE', {})
        commit('TYPER_ACTIVE', true)
        commit('ACCEPTED_TERMS', true)
        dispatch('uistate/checkAgentAvailable', { state }, {root: true})
        dispatch('uistate/setTimeClosingNotifications', {}, {root: true})
      }
      const channel = get(this._vm, '$chatApi.settings.channel')
      commit('SET_CHANNEL', channel)
      // }
    },
    connect ({ commit, state, dispatch, rootState, rootGetters }) {
      const channel = get(this._vm, '$chatApi.settings.channel')

      registerCallbacks({ state, commit, dispatch, chatApi: this._vm.$chatApi })

      dispatch('uistate/setConversationState',
        'STARTING_NEW_CONVERSATION',
        { root: true }
      )

      commit('SET_DISABLE_SEND_BUTTON', {status: true})

      if (channel !== 'WEBSITE') {
        // AiaiBotStoryId is currently hardcoded but in the future, it should be defined depending on the page on which the user opens the chat.
        dispatch('setAiaiBotStoryId', channel === 'KUPOWEB'
          ? process.env.VUE_APP_AIAIBOT_DEFAULT_STORY_ID_KUPOWEB
          : process.env.VUE_APP_AIAIBOT_DEFAULT_STORY_ID_KUPOAPP
        )
      }

      const aiaiBotStoryId = rootGetters['uistate/isAiaiBotActive'] &&
        state.aiaiBotStoryId

      return this._vm.$chatApi.connect(rootState.uistate.lang, state.trigger, aiaiBotStoryId)
        .then(({ user, conversation }) => {

          dispatch('uistate/setConversationState', 'READY', { root: true })
          commit('SET_USER_PROFILE', user)
          commit('SET_DISABLE_SEND_BUTTON', {status: false})

          if (
            !rootGetters['uistate/isCustomerFlow'] &&
            state.conversation?.messages?.length === 1
          ) {
            conversation.messages.unshift(state.conversation.messages[0])
          }

          // sanitize conversation messages
          commit('ACCEPTED_TERMS', true)
          dispatch('uistate/checkAgentAvailable', {}, { root: true })

          conversation.messages = conversation.messages
            .filter((message) => {
              // malformated file message
              if (message.file && (!message.files || message.files.length === 0)) {
                return false
              }

              return true
            })

          listenToCustomEvent(EVENT.LISTEN_TO.UPDATE_BASKET, (e) => {
            // console.log('event "sanitas_ovp-basket_updated" intercepted')
            dispatch('updateBasket', e.detail).then(() => dispatch('sendBasket'))
            // console.log('update basket state and send basket data to backend')
          })

          if (conversation.status !== 'AGENT_ENDED') {
            dispatchCustomEvent(EVENT.SEND.CONVERSATION_STARTED)
          }

          commit('SET_CONVERSATION', conversation)

          // TODO Aiaibot => in conversation's messages endpoint backend should provide frontend the payload at the upper level so we know what payloadType we're having...
          const lastMessage = conversation
            .messages[conversation.messages.length - 1]

          if (lastMessage?.msgType === 'AIAIBOT') {
            const isIntent = lastMessage.aiaibotPayload &&
              Object.keys(lastMessage.aiaibotPayload).length === 0

            const isUserInput = lastMessage.aiaibotPayload &&
              Object.keys(lastMessage.aiaibotPayload).includes('inputType')

            if (isIntent || isUserInput) {
              dispatch('setShowAiaiBotInput', true)
              dispatch('setAiaiBotInputConfig', {
                widgetType: isIntent ? 'intent' : 'user-input',
                config: {
                  ...AIAIBOT_INPUT_DEFAULT_CONFIG,
                  ...lastMessage.aiaibotPayload
                }
              })
            }
          }

          if (
            conversation.status === 'AIAIBOT_ENDED' &&
            conversation.conversationType === 'AIAIBOT' &&
            lastMessage?.msgType === 'AIAIBOT_INACTIVE_CLOSE'
          ) {
            dispatch('handleInactiveConversationEnded')
          } else if (['AGENT_ENDED', 'AIAIBOT_ENDED'].includes(conversation.status)) {
            dispatch('handleConversationEnded')
          } else if(
            rootGetters['uistate/isAiaiBotActive'] &&
            state.aiaiBotStoryId &&
            conversation.conversationType === 'CHAT' &&
            conversation.participants?.length === 1 &&
            conversation.messages.length
          ) {
            dispatch('handleConversationHandover')
          }
          else if (state.conversation.participants
            .some(participant => participant.agent)
          ) {
            let participantAgent = state.conversation.participants.find(participant => participant.agent)

            commit('SET_ACTIVE_AGENT', participantAgent)

            if (participantAgent.agent) {
              commit('SET_AGENT_STATUS', participantAgent)
            }
          }

          dispatch('sendUrl')

          return dispatch('initUnreadMessages')
        })
    },
    async handleAiaiBotConversationStartFailed ({ commit, dispatch }) {
      commit('SET_WAITING_ON_AIAIBOT', false)

      await dispatch('clearAiaiBotSessionId')
      await dispatch('uistate/refuseAiaiBotDisclaimer', {}, { root: true })
      await dispatch('reset')
      await dispatch('setAiaiBotStoryId', null)

      commit('uistate/SET_CONVERSATION_STATE', 'READY', { root: true })
    },
    async handleInactiveConversationEnded ({ state, getters, commit, dispatch }) {
      const agentScreenName = getters.automaticMessageAgentScreenName

      if (!state.chatbotActive) {
        await dispatch('messageReceived', {
          msgType: 'AIAIBOT_FINISH',
          screenName: agentScreenName
        })

        await dispatch('removeAgentFromConversation')
        await dispatch('setShowAiaiBotInput', false)

        commit('ADD_MESSAGE', {
          msgLabelKey: 'aiaibot_message_inactive_closed',
          msgType: 'CHAT'
        })

        await dispatch('uistate/continueForCustomer', {}, { root: true })
      }
    },
    handleConversationEnded ({ state, getters, dispatch }) {
      const agentScreenName = getters.automaticMessageAgentScreenName

      if (!state.chatbotActive) {
        dispatch('messageReceived', {
          msgType: 'AIAIBOT_FINISH',
          screenName: agentScreenName
        })
      }

      dispatch('removeAgentFromConversation')
      dispatch('uistate/continueForCustomer', {}, { root: true })
    },
    handleConversationHandover ({ commit, dispatch, rootState }) {
      dispatch('messageReceived', {
        msgType: 'AIAIBOT_FINISH',
        agentName: AIAIBOT_AGENT.agentName,
        screenName: AIAIBOT_AGENT.screenName
      })

      this._vm.$chatApi.getChatAvailable(rootState.uistate.lang)
        .then(response => {
          if (response.isOpeningHours) {
            commit('ADD_MESSAGE', {
              msgLabelKey: 'aiaibot_live_chat_welcome',
              msgType: 'CHAT'
            })
          } else {
            commit('ADD_MESSAGE', {
              msgText: response.outsideServiceHoursMessage,
              msgType: 'CHAT'
            })
          }
        })
    },
    reset ({ commit, rootGetters }) {
      if (rootGetters['uistate/isCustomerFlow']) {
        let conv = {
          convId: -1,
          status: 'INIT',
          mine: true
        }
        commit('SET_CONVERSATION', conv)
        commit('uistate/SET_CONVERSATION_STATE', 'INIT', { root: true })
      } else {
        commit('SET_CONVERSATION', null)
      }
      commit('SET_ACTIVE_AGENT')
      commit('SET_AGENT_STATUS')
      commit('SET_TRIGGER', null)
      commit('CHATBOT_ACTIVE', {status: false})
      commit('SET_WAIT_MESSAGE_SHOWN', false)
      commit('SET_USER_PROFILE', {})
      commit('SET_UNREAD_MESSAGES', 0)
      commit('uistate/SET_TRANSCRIPT_EMAIL', '', { root: true })
      dispatchCustomEvent(EVENT.SEND.CONVERSATION_ENDED)
    },
    suspendConversation ({ state, commit }) {
      if (state.conversation && state.conversation.convId !== -1) {
        return this._vm.$chatApi.suspendConversation(state.conversation)
          .finally(() => {
            commit('uistate/SET_CONVERSATION_STATE', 'CONVERSATION_SUSPENDED', {root: true})
            commit('TYPER_ACTIVE', false)
          })
      } else {
        commit('uistate/SET_CONVERSATION_STATE', 'CONVERSATION_RATED', {root: true})
      }
    },
    resumeConversation ({ state, commit }) {
      return this._vm.$chatApi.resumeConversation(state.conversation)
        .finally(() => {
          commit('uistate/SET_CONVERSATION_STATE', 'CONVERSATION', {root: true})
          commit('TYPER_ACTIVE', true)
        })
    },
    openRating ({ commit, state, rootGetters }, mode) {
      let promise
      if (mode === 'enduser') {
        promise = this._vm.$chatApi.endConversation(state.conversation)
      } else {
        promise = Promise.resolve(true)
      }

      return promise.then(() => {
        if (rootGetters['uistate/isCustomerFlow']) {
          commit('uistate/SET_CONVERSATION_STATE', 'CONVERSATION_RATING', {root: true})
        } else {
        // if there is less than 10 messages in the conversation with type chat, we proceed directly to the end
          let userInteractionsCount = state.conversation.messages.filter(message => message.msgType !== 'CHATBOT').length
          if (userInteractionsCount < 10) {
            commit('uistate/SET_CONVERSATION_STATE', 'CONVERSATION_RATED', {root: true})
          } else {
            commit('uistate/SET_CONVERSATION_STATE', 'CONVERSATION_RATING', {root: true})
          }
        }
        return true
      })
    },
    initUnreadMessages ({ commit, state, dispatch, rootState, getters, rootGetters }) {
      if (!state.conversation || rootState.uistate.windowOpen) {
        const latestMsgIndex = state.conversation?.messages.length - 1

        if (state.conversation?.messages[latestMsgIndex]?.status !== 'SEEN') {
          dispatch('updateMessagesStatus')
        }

        return commit('SET_UNREAD_MESSAGES', 0)
      }

      commit('SET_UNREAD_MESSAGES', getters.getUnreadMessages.length)
      dispatchLatestUnreadMessage(getters.getUnreadMessages, rootGetters['uistate/chatIsOpen'])

      return true
    },
    resetUnreadMessages ({ commit }) {
      commit('SET_UNREAD_MESSAGES', 0)
    },
    updateMessagesStatus ({ state, commit }) {
      if (!state.conversation.messages) {
        return
      }

      for (let i = state.conversation.messages.length - 1; i >= 0; i--) {
        if (state.conversation.messages[i].status === 'SEEN') { break }

        let payload = {
          sender: state.user.userId,
          msgId: state.conversation.messages[i].msgId,
          content: 'SEEN',
          type: 'MESSAGE_STATUS'
        }

        if (state.activeAgent) {
          const recipient = { recipient: state.activeAgent.userId ? state.activeAgent.userId : state.activeAgent.senderId }
          Object.assign(payload, recipient)
        }

        commit('UPDATE_MESSAGE_STATUS', {
          messageId: payload.msgId,
          status: 'SEEN'
        })

        this._vm.$chatApi.sendConversationEvent(state.conversation.convId, payload)
      }
    },
    addAgentToConversation ({ commit }, payload) {
      commit('SET_ACTIVE_AGENT', payload)
    },
    removeAgentFromConversation ({ commit }) {
      commit('SET_ACTIVE_AGENT')
      commit('SET_AGENT_STATUS')
    },
    updateBasket ({ commit }, payload) {
      commit('SET_BASKET', payload)
    },
    disagree ({ commit, getters }) {
      let conv = {
        convId: -1,
        status: 'INIT',
        mine: true,
        messages: getters.disagreeMessages
      }
      commit('SET_CONVERSATION', conv)
      commit('SET_USER_PROFILE', {})
      commit('TYPER_ACTIVE', false)
      commit('ACCEPTED_TERMS', false)
    },
    sendDataTransferFlag ({ state }, payload) {
      return this._vm.$chatApi.sendDataTransferFlag(state.conversation.convId, payload)
    },
    sendUserIsTyping ({state}, value) {
      if (state.user.userId && state.conversation.convId !== -1) {
        let message = {
          sender: state.user.userId,
          recipient: state.activeAgent ? state.activeAgent.userId || state.activeAgent.senderId : null,
          content: value,
          type: 'ENDUSER_TYPING'
        }
        return this._vm.$chatApi.sendConversationEvent(state.conversation.convId, message)
      }
    },
    sendUserIsNotTyping ({state}) {
      if (state.user.userId && state.conversation.convId !== -1) {
        let message = {
          sender: state.user.userId,
          recipient: state.activeAgent ? state.activeAgent.userId || state.activeAgent.senderId : null,
          type: 'ENDUSER_NOT_TYPING'
        }
        return this._vm.$chatApi.sendConversationEvent(state.conversation.convId, message)
      }
    },
    showFirstNotification ({ commit }) {
      let messageObject = {
        convId: -1,
        msgId: -200,
        msgLabelKey: 'first_time_close_notification',
        senderId: -2000,
        screenName: 'Sanitas',
        msgType: 'CHAT'
      }
      commit('ADD_MESSAGE', messageObject)
    },
    showCloseTimeNotification ({ commit }) {
      let messageObject = {
        convId: -1,
        msgId: -201,
        msgLabelKey: 'close_time_notification',
        senderId: -2000,
        screenName: 'Sanitas',
        msgType: 'CHAT'
      }
      commit('ADD_MESSAGE', messageObject)
      commit('SET_DISABLE_SEND_BUTTON', {status: true})
    },
    setClosingTime ({ commit }, payload) {
      commit('SET_CLOSING_TIME', payload)
    },
    setAcceptedTerms ({ commit }, payload) {
      commit('ACCEPTED_TERMS', payload)
    },
    uploadFile ({ dispatch, state }, file) {
      let formData = new FormData()
      let channel = get(this._vm, '$chatApi.settings.channel')
      formData.append('webFile', file)
      formData.append('convId', state.conversation.convId)
      formData.append('channel', channel)
      this._vm.$chatApi.uploadFile(formData)
        .then(() => {
          console.log('Ok')
        })
        .catch(() => {
          dispatch('addMessage', {
            convId: -1,
            msgLabelKey: 'messages_acceptedFile',
            senderId: -2000,
            screenName: 'Sanitas',
            msgType: 'CHAT'
          })
        })
    },
    setTrigger ({ commit }, value) {
      commit('SET_TRIGGER', value)
    },
    chatbotActive ({ commit }, payload) {
      commit('CHATBOT_ACTIVE', payload)
      commit('CHATBOT_INCLUDED', {status: true})
    },
    addMessage ({ commit }, message) {
      commit('ADD_MESSAGE', message)
    },
    conversationClose ({ dispatch }) {
      dispatch('uistate/setConversationState', 'CONVERSATION_NEW', {root: true})
    },
    archiveConversation ({state}) {
      return this._vm.$chatApi.archiveConversation(state.conversation.convId)
    },
    transcriptUi ({dispatch, commit}) {
      commit('uistate/SET_TRANSCRIPT_STATE', 'form', { root: true })
      dispatch('archiveConversation')
        .then(() => {
          dispatch('uistate/setConversationState', 'CONVERSATION_ENDED', { root: true })
        }
      )
    },
    setArchived ({ commit }) {
      Vue.prototype.$chatApi.getMyArchivedConversations()
        .then(response => {
          commit('SET_ARCHIVED', response)
          commit('SET_ARCHIVED_LOADING', false)
        })
        .catch(err => {
          commit('SET_ARCHIVED_LOADING', false)
          return err
        })
    },
    setSelectedArchived ({ commit }, payload) {
      commit('SET_SELECTED_ARCHIVED', payload)
    },
    setSelectedArchivedMessages ({ commit }, payload) {
      commit('SET_SELECTED_ARCHIVED_MESSAGES', payload)
    },
    setLastCheckActivityMsgStatus ({ commit }, payload) {
      commit('SET_LAST_CHECK_ACTIVITY_MSG_STATUS', payload)
    },
    setAiaiBotSessionId ({ commit }, value) {
      commit('SET_AIAIBOT_SESSION_ID', value)
    },
    clearAiaiBotSessionId({ commit }) {
      commit('CLEAR_AIAIBOT_SESSION_ID')
    },
    setAiaiBotStoryId({ commit }, value) {
      commit('SET_AIAIBOT_STORY_ID', value)
    },
    updateUnreadMessages({ getters, commit, rootGetters }) {
      const countUnreadMessages = getters.getUnreadMessages.length

      commit('SET_UNREAD_MESSAGES', countUnreadMessages)

      dispatchLatestUnreadMessage(getters.getUnreadMessages, rootGetters['uistate/chatIsOpen'])
    },
    setShowAiaiBotInput ({ commit }, value) {
      commit('SET_SHOW_AIAIBOT_INPUT', value)
    },
    setAiaiBotInputConfig ({ commit }, value) {
      commit('SET_AIAIBOT_INPUT_CONFIG', value)
    },
    resetAiaiBotInputConfig ({ commit }) {
      commit('SET_AIAIBOT_INPUT_CONFIG', {})
    },
    async setIsNonCustomer({ commit }) {
      const userInfo = await Vue.prototype.$chatApi.getUserInfo()
      commit('SET_IS_NON_CUSTOMER', !userInfo.customerId)
    }
  },
  mutations: {
    SET_USER_PROFILE (state, payload) {
      // state.user = omit(payload, ['links'])
      state.user = payload
    },
    SET_CONVERSATION (state, payload) {
      state.conversation = payload
    },
    ADD_MESSAGE (state, payload) {
      let conversation = state.conversation
      if (conversation?.messages) {
        conversation.messages.push(payload)
      }

      Vue.set(state, 'conversation', cloneDeep(conversation))
    },
    SET_UNREAD_MESSAGES (state, payload) {
      state.unreadMessages = payload
    },
    SET_API_CALLBACK_REGISTERED (state, value) {
      state.apiCallbackRegistered = value
    },
    SET_ACTIVE_AGENT (state, payload = null) {
      state.activeAgent = payload
    },
    SET_BASKET (state, payload) {
      state.basket = { ...payload }
    },
    TYPER_ACTIVE (state, value) {
      state.typerActive = value
    },
    ACCEPTED_TERMS (state, value) {
      state.acceptedTerms = value
    },
    SET_DISABLE_SEND_BUTTON (state, payload) {
      state.disableSendButton = payload.status
    },
    SET_CLOSING_TIME (state, payload) {
      state.closingTime = payload.closingTime
    },
    SET_TRIGGER (state, value) {
      state.trigger = value
    },
    CHATBOT_ACTIVE (state, payload) {
      state.chatbotActive = payload.status
    },
    CHATBOT_INCLUDED (state, payload) {
      state.chatbotIncluded = payload.status
    },
    SET_WAIT_MESSAGE_SHOWN (state, value) {
      state.waitMessageShown = value
    },
    SET_CONVERSATION_STATUS (state, value) {
      state.conversation.status = value
    },
    ANSWER_QUESTION (state, payload) {
      let answeredMsg = state.conversation.messages.find(message => message.msgId === payload.msgId)
      if (answeredMsg) {
        answeredMsg.answer = payload.answer
      }
    },
    ADD_CHATBOT_ANSWERED_QUESTION (state, payload) {
      state.chatbotAnsweredQuestions.push(payload)
    },
    SET_CHANNEL (state, value) {
      state.channel = value
    },
    SET_RATING (state, value) {
      state.rating = { ...value }
    },
    SET_AGENT_STATUS (state, value = { status: '' }) {
      state.agentStatus = value.status
    },
    SET_ARCHIVED (state, value) {
      state.archived = value
        .sort((a,b) => a.created > b.created
            ? 1
            : a.created === b.created
              ? 0
              : -1)
    },
    SET_ARCHIVED_LOADING (state, value) {
      state.archivedLoading = value
    },
    SET_SELECTED_ARCHIVED (state, value) {
      state.selectedArchived = value
    },
    SET_LAST_CHECK_ACTIVITY_MSG_STATUS (state, value) {
      state.lastCheckActivityActiveMsg = value
    },
    UPDATE_MESSAGES (state, messages) {
      state.conversation.messages = messages
    },
    UPDATE_MESSAGE_STATUS (state, { messageId, status }) {
      state.conversation.messages = state.conversation.messages
        .map(message => ({
          ...message,
          ...(message.msgId === messageId ? { status } : {})
        }))
    },
    SET_AIAIBOT_SESSION_ID (state, sessionId) {
      if (state.conversation) {
        state.conversation.aiaiBotSessionId = sessionId
      }
    },
    CLEAR_AIAIBOT_SESSION_ID (state) {
      if (state.conversation?.aiaiBotSessionId) {
        state.conversation.aiaiBotSessionId = null
      }
    },
    SET_AIAIBOT_STORY_ID (state, aiaiBotStoryId) {
      state.aiaiBotStoryId = aiaiBotStoryId
    },
    SET_SHOW_AIAIBOT_INPUT (state, value) {
      state.showAiaiBotInput = value
    },
    SET_AIAIBOT_INPUT_CONFIG (state, value) {
      state.aiaiBotInputConfig = value
    },
    SET_WAITING_ON_AIAIBOT (state, value) {
      state.waitingOnAiaiBot = value
    },
    SET_IS_NON_CUSTOMER (state, value) {
      state.isNonCustomer = value
    },
    SET_SELECTED_ARCHIVED_MESSAGES (state, value) {
      state.selectedArchivedMessages = value
    }
  }
}
